import React, { FC } from 'react'
import { HStack } from '@chakra-ui/react'

import { useInputFocus } from '../..'
import { InputProps, Input } from '../Input'

export type SupportedDateFormat = 'DD-MM-YYYY' | 'MM-DD-YYYY'

export type DateProps = {
  onDateChange?: (ISO8601date: string) => void
  onChange: (ISO8601date: string) => void
  // year-month-day
  value: string | null
  // We pass through the name so formik can track the value on blur/focus events
  name?: string
  isInvalid?: boolean
  dataCy?: string
  format?: SupportedDateFormat
} & Pick<InputProps, 'variant' | 'placeholder' | 'onBlur' | 'onFocus'>

export const DateInput: FC<DateProps> = ({
  variant,
  value = null,
  onChange,
  onBlur,
  onFocus,
  onDateChange = () => {},
  isInvalid,
  name,
  dataCy,
  format = 'DD-MM-YYYY',
}) => {
  const { handleFocus, handleBlur } = useInputFocus({ onBlur, onFocus })
  const [year = '', month = '', day = ''] = (value ?? '').split('-')

  const handleChange = (newDate: string) => {
    onDateChange(newDate)
    onChange(newDate)
  }

  const handleDayChange = (newDay: string) => {
    handleChange(`${year}-${month}-${newDay}`)
  }
  const handleMonthChange = (newMonth: string) => {
    handleChange(`${year}-${newMonth}-${day}`)
  }
  const handleYearChange = (newYear: string) => {
    handleChange(`${newYear}-${month}-${day}`)
  }

  const renderInputs = () => {
    const tokens = format.split('-')

    return tokens.map((token) => {
      switch (token) {
        case 'DD':
          return (
            <Input
              variant={variant}
              value={day}
              onChange={(e) => handleDayChange(e.target.value)}
              placeholder="DD"
              flex={1}
              letterSpacing="0.5em"
              inputMode="numeric"
              isInvalid={isInvalid}
              onFocus={handleFocus}
              onBlur={handleBlur}
              name={name}
              maxLength={2}
              data-cy="date-day"
            />
          )
        case 'MM':
          return (
            <Input
              variant={variant}
              isInvalid={isInvalid}
              value={month}
              onChange={(e) => handleMonthChange(e.target.value)}
              placeholder="MM"
              flex={1}
              letterSpacing="0.5em"
              inputMode="numeric"
              onFocus={handleFocus}
              onBlur={handleBlur}
              name={name}
              maxLength={2}
              data-cy="date-month"
            />
          )
        case 'YYYY':
          return (
            <Input
              variant={variant}
              isInvalid={isInvalid}
              value={year}
              onChange={(e) => handleYearChange(e.target.value)}
              placeholder="YYYY"
              flex={1}
              letterSpacing="0.5em"
              inputMode="numeric"
              onFocus={handleFocus}
              onBlur={handleBlur}
              name={name}
              maxLength={4}
              data-cy="date-year"
            />
          )
        default:
          return undefined
      }
    })
  }

  return (
    <HStack spacing="sm" data-cy={dataCy} width="100%">
      {renderInputs()}
    </HStack>
  )
}
